import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Input } from '../../../../../../components/ui/input';
import { Label } from '../../../../../../components/ui/label';
import { Cable } from 'lucide-react';
import { DashboardProject } from '../../../../types/project';
import type { PipeSize, SetupState, SetupState1, PipeSize1, FabricationSetupData } from '../../../../types/setup';
import { CommonSetupForm } from './CommonSetupForm';
import { NumberInput } from '../../../../../../components/ui/NumberInput';

interface PipeInPipeSetupProps {
  project: DashboardProject;
  manufacturingList?: any[];
  setManufacturingList: React.Dispatch<React.SetStateAction<any[]>>;
  unit: string;
  setUnit?: (unit: string) => void;
  showValidation?: boolean;
  setupData?: FabricationSetupData;
  state?: SetupState1;
  setState?: React.Dispatch<React.SetStateAction<SetupState1>>;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  canEdit?: boolean;
  onSavePipe?: (pipeIndex: number) => Promise<boolean>;
}

export interface PipeInPipeSetupRef {
  getSetupData: () => SetupState1;
}

export const PipeInPipeSetup = forwardRef<PipeInPipeSetupRef, PipeInPipeSetupProps>(({ project, manufacturingList, setManufacturingList, unit, setUnit, showValidation = false, setupData, state: propState, setState: propSetState, setHasUnsavedChanges, canEdit = true, onSavePipe }, ref) => {
  // Use prop state if provided, otherwise use internal state
  const [internalState, setInternalState] = useState<SetupState1>({
    numberOfPipeSizes: 1,
    projectId: parseInt(project.project.projectId),
    projectTypeId: project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id || 0,
    projectType: 'fabrication',
    projectSubType: 'pipeInPipe',
    pipeSizes: [{
      pipeId: 0,
      pipeName: "Pipe 1",
      pipeSize: 0,
      wallThickness: 0,
      numberOfBuckleArrestors: 0,
      numberOfSpools: 0,
      numberOfPipes: 0,
      pipeLength: 0,
      numberOfForges: 0,
      outputAssemblyLength: 0,
      manufacturerId: 0,
      manufacturerIds: [],
      pipeOrder: 1,
      isActive: true,
      prefixAssemblyName: 'Assy',
      groupId: '',
      jobNumber: {
        numberOfJobs: 1,
        jobNumbers: ['']
      },
      documentsData: {
        numberOfDocs: 0,
        isDocumentsAvailable: false,
        documentIds: [],
        documentFileNames: []
      }
    }]
  });

  // Use prop state if provided, otherwise use internal state
  const state = propState || internalState;
  const setState = propSetState || setInternalState;


  // Expose setup data through ref
  useImperativeHandle(ref, () => ({
    getSetupData: () => state
  }));

  // Validation function for additional fields
  const validateField = (value: any): boolean => {
    if (value === null || value === undefined || value === '' || value === 0) {
      return false;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return false;
    }
    return true;
  };

  const additionalFields = (pipe: PipeSize1, index: number, helpers?: {
    handleUnitChange: (index: number, field: keyof { pipeSize: string; wallThickness: string; length: string; outputAssemblyLength: string }, value: string) => void;
    UNIT_OPTIONS: { value: string; label: string }[];
    canEdit?: boolean;
    showValidation?: boolean;
    validateField?: (value: any) => boolean;
    isChangingPipeCount?: boolean;
  }) => (
    <>
      <div>
        <Label className="text-[12px] font-medium text-text-grey" htmlFor={`numberOfForges-${index}`}>Number of Forges <span className="text-red-500">*</span></Label>
        <NumberInput
          id={`numberOfForges-${index}`}
          min={0}
          step={1}
          value={pipe.numberOfForges}
          onChange={(value) => {
            if (!canEdit) return; // Prevent changes when canEdit is false
            setState(prev => ({
              ...prev,
              pipeSizes: prev.pipeSizes.map((p, i) => 
                i === index ? { ...p, numberOfForges: parseInt(value) || 0 } : p
              )
            }));
          }}
          className={`mt-2 ${showValidation && !validateField(pipe.numberOfForges) ? '' : ''}`}
          placeholder="Enter number of forges"
          disabled={!canEdit}
        />
      </div>
      <div>
        <Label className="text-[12px] font-medium text-text-grey" htmlFor={`outputAssemblyLength-${index}`}>Expected Output Assembly Length <span className="text-red-500">*</span></Label>
        <NumberInput
          id={`outputAssemblyLength-${index}`}
          min={0}
          step={0.01}
          value={pipe.outputAssemblyLength}
          onChange={(value) => {
            if (!canEdit) return; // Prevent changes when canEdit is false
            setState(prev => ({
              ...prev,
              pipeSizes: prev.pipeSizes.map((p, i) =>
                i === index ? { ...p, outputAssemblyLength: parseFloat(value) || 0 } : p
              )
            }));
          }}
          {...(helpers?.UNIT_OPTIONS && helpers?.handleUnitChange
            ? {
                showUnitSelect: true,
                unitValue: pipe.units?.outputAssemblyLength || unit,
                unitOptions: helpers.UNIT_OPTIONS,
                onUnitChange: (v: string) => helpers.handleUnitChange(index, 'outputAssemblyLength', v),
                unitSelectWrapperClassName: 'mt-2',
                className:
                  showValidation && !validateField(pipe.outputAssemblyLength) ? 'border-red-500' : '',
              }
            : {
                className: `mt-2 ${showValidation && !validateField(pipe.outputAssemblyLength) ? 'border-red-500' : ''}`,
              })}
          placeholder="Enter expected output assembly length"
          disabled={!canEdit}
        />
      </div>
      <div>
        <Label className="text-[12px] font-medium text-text-grey" htmlFor={`prefixAssemblyName-${index}`}>Assembly Name (Prefix) <span className="text-red-500">*</span></Label>
        <Input
          id={`prefixAssemblyName-${index}`}
          value={pipe.prefixAssemblyName || ''}
          onChange={(e) => {
            if (!canEdit) return; // Prevent changes when canEdit is false
            setState(prev => ({
              ...prev,
              pipeSizes: prev.pipeSizes.map((p, i) =>
                i === index ? { ...p, prefixAssemblyName: e.target.value } : p
              )
            }));
            setHasUnsavedChanges(true);
          }}
          className={`mt-2 ${showValidation && !validateField(pipe.prefixAssemblyName) ? '' : ''}`}
          placeholder="Enter prefix assembly name"
          disabled={!canEdit}
        />
        {showValidation && !validateField(pipe.prefixAssemblyName) && (
          <p className="mt-1 text-xs text-red-500">Prefix assembly name is required</p>
        )}
      </div>
    </>
  );

  return (
    <CommonSetupForm
      project={project}
      state={state}
      setState={setState}
      title="Pipe in Pipe"
      icon={<Cable className="h-5 w-5" />}
      iconColor="text-purple-800"
      additionalFields={additionalFields as any}
      manufacturingList={manufacturingList}
      setManufacturingList={setManufacturingList}
      unit={unit}
      setUnit={setUnit}
      showValidation={showValidation}
      setHasUnsavedChanges={setHasUnsavedChanges}
      onSavePipe={onSavePipe}
      canEdit={canEdit}
    />
  );
}); 